import type { DaoError, IEditableDataSource } from "@/data/dao/service/BaseServiceDao";
import { Format } from "@/data/util/Format";



import { logError } from "@/shared/logging/errors";
import { style } from "@/shared/ui/Style";



import { i18n } from "@/portal/Trans";
import { DomUtils } from "@/portal/helpers/DomUtils";
import { UiNotification } from "@/portal/ui/Notification";


export abstract class BaseEntityForm<M, D> {
    private $element: JQuery;
    protected $saveBtn: JQuery;
    private $removeBtn?: JQuery;

    private hasHiddenWriteActions = false;

    protected $form: JQuery;

    protected id?: string;
    protected copyObjectId?: string;
    protected dataSource: IEditableDataSource<M>;
    protected redirectPathProvider?: { (): string };

    public onNameChanged?: { (newName: string) };
    public onSaved?: { (newModel: M) };
    public onDeleted?: { () };
    public onCreateError?: { (error: DaoError) };

    protected isReady = false;
    protected useRedirect = true;
    protected saveAndCopy = false;
    private isLoading = false;

    constructor(element: JQuery, dataSource: IEditableDataSource<M>) {
        this.$element = element;
        this.dataSource = dataSource;

        this.$form = this.$element.find("> .kt-form");
        this.$saveBtn = this.$form.find('> .kt-portlet__foot > .kt-form__actions .btn[type="submit"]');

        this.$removeBtn = this.$form.find("> .kt-portlet__foot > .kt-form__actions .removebtn").hide();
        if (this.$removeBtn.length === 0) this.$removeBtn = undefined;
        else {
            this.$removeBtn.click((e) => {
                e.preventDefault();
                this.delete();
            });
        }

        this.$form.submit((e) => {
            e.preventDefault();
            e.stopPropagation();
            this.save();
        });

        this.$saveBtn.click((e) => {
            e.preventDefault();
            this.redirectPathProvider = undefined;
            this.saveAndCopy = false;
            this.$form.submit();
        });

        //check for dropdown actions
        const btnGroup = this.$saveBtn.parent();
        if (btnGroup.hasClass("btn-group")) {
            btnGroup.find("> .dropdown-menu > .dropdown-item").click((e) => {
                e.preventDefault();
                const t = $(e.currentTarget);
                t.parent()["dropdown"]("hide");

                const dataAction = t.attr("data-action");
                this.saveAndCopy = dataAction === "save_and_copy";

                if (dataAction === "saveandcreate") {
                    this.redirectPathProvider = () => this.getBasePath() + "new";
                    this.useRedirect = true;

                    this.$form.submit();
                } else if (dataAction === "save_and_copy") {
                    this.redirectPathProvider = () => this.getBasePath();
                    this.$form.submit();
                }
                return false;
            });
        }
    }

    public setData(model?: M, copyObjectId?: string) {
        this.copyObjectId = copyObjectId;
        if (model == undefined || model["id"] === undefined) {
            this.id = undefined;

            if (this.$removeBtn !== undefined) this.$removeBtn.hide();
        } else {
            this.id = model["id"];
            if (this.$removeBtn !== undefined && !this.hasHiddenWriteActions) this.$removeBtn.show();
        }

        this.isReady = true;
        this.setFormData(model);
    }

    public hideWriteActions(isHidden: boolean) {
        this.hasHiddenWriteActions = isHidden;

        if (isHidden) {
            this.$saveBtn.hide();
            this.$removeBtn!.hide();
        } else {
            this.$saveBtn.show();
            this.$removeBtn!.show();
        }
    }

    protected abstract setFormData(model?: M);
    public abstract getFormData($form: JQuery): D | undefined;

    protected updateName(newName: string) {
        if (this.onNameChanged !== undefined)
            this.onNameChanged(newName === undefined ? "" : Format.htmlEscape(newName)!);
    }

    protected setLoading(isLoading: boolean) {
        this.isLoading = isLoading;
        if (isLoading) {
            this.$saveBtn.addClass(style.buttonLoaderClass).prop("disabled", true).find("> .la").remove();

            if (this.$removeBtn !== undefined) this.$removeBtn.hide();
        } else {
            this.$saveBtn.removeClass(style.buttonLoaderClass).prop("disabled", false);
            if (this.$saveBtn.find("> .la").length === 0) this.$saveBtn.prepend('<i class="la la-save" />');

            if (this.$removeBtn !== undefined && this.id !== undefined) this.$removeBtn.show();
        }
    }

    private save() {
        // a disabled or hidden save button does not stop a submit by the Enter key
        if (!this.isReady || this.isLoading || this.hasHiddenWriteActions) return;

        this.$form.find(".is-invalid").removeClass("is-invalid");
        const fData = this.getFormData(this.$form);
        if (fData === undefined || fData == null) return;

        fData["copyObjectId"] = this.copyObjectId;
        this.setLoading(true);
        this.confirmSave(fData).then((isConfirmed) => {
            if (!isConfirmed) {
                this.setLoading(false);
                return;
            }

            if (this.id !== undefined) {
                this.update(this.id, fData, this.saveAndCopy);
            } else {
                this.create(fData, this.saveAndCopy);
            }
        });
    }

    // lets a form ask the user before the data is sent - resolving false cancels the save
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    protected confirmSave(data: D): Promise<boolean> {
        return Promise.resolve(true);
    }

    private update(id: string, fData: D, createCopy: boolean) {
        this.setLoading(true);

        this.dataSource
            .update(id, fData)
            .then((response) => {
                return this.handlePostSaveAction(response).then((response) => {
                    if (createCopy) {
                        this.copySaved(response);
                        return;
                    }

                    this.setFormData(response);
                    this.showSuccess(i18n("general.successfullyCreated"));

                    if (this.onSaved !== undefined) this.onSaved(response);

                    // setting location.href does not stop the script, so the form stays locked until the page unloads
                    if (this.useRedirect && this.redirectPathProvider !== undefined) {
                        location.href = this.redirectPathProvider();
                        return;
                    }

                    this.setLoading(false);
                });
            })
            .catch((ex) => {
                logError(ex);
                this.setLoading(false);
                this.handleApiError("update", ex);
            });
    }

    private create(fData: D, createCopy: boolean) {
        this.setLoading(true);

        this.dataSource
            .create(fData)
            .then((response) => {
                this.id = response["id"];
                return this.handlePostSaveAction(response).then((response) => {
                    if (createCopy) {
                        this.copySaved(response);
                        return;
                    }

                    this.setFormData(response);
                    this.showSuccess(i18n("general.successfullyUpdated"));
                    if (this.onSaved !== undefined) this.onSaved(response);

                    // setting location.href does not stop the script, so the form stays locked until the page unloads
                    if (this.useRedirect) {
                        if (this.redirectPathProvider !== undefined) location.href = this.redirectPathProvider();
                        else location.href = this.getBasePath() + response["id"];
                        return;
                    }

                    this.setLoading(false);
                });
            })
            .catch((ex) => {
                logError(ex);
                this.setLoading(false);

                // created, but the step after it failed: the address names the entity from now on, as after a
                // successful create, so a reload opens it instead of an empty new one
                if (this.id !== undefined && this.useRedirect)
                    history.replaceState(history.state, "", this.getBasePath() + this.id);

                if (this.onCreateError != null) {
                    this.onCreateError(ex);
                } else {
                    this.handleApiError("create", ex);
                }
            });
    }

    // "Save and Copy" after the save: by default opens a new entity prefilled from the saved one, the form stays
    // locked until the page unloads
    protected copySaved(model: M) {
        location.href = this.getBasePath() + "new" + "?objectIdToCopy=" + model["id"];
    }

    private delete() {
        if (!this.isReady) return;

        if (this.id === undefined) return;

        UiNotification.confirm(i18n("general.reallyRemove"), undefined, () => {
            this.setLoading(true);
            this.dataSource
                .remove(this.id!)
                .then(() => {
                    this.showSuccess(i18n("general.successfullyRemoved"));
                    this.setLoading(false);

                    if (this.onDeleted !== undefined) this.onDeleted();

                    if (this.useRedirect) this.goBack();
                })
                .catch((ex) => {
                    logError(ex);
                    this.handleApiError("delete", ex);
                    this.setLoading(false);
                });
        });
    }

    protected showSuccess(message: string) {
        UiNotification.showSuccess(message);
    }

    protected goBack() {
        location.href = this.getBasePath();
    }

    protected getBasePath(): string {
        let url: string;
        const backLink = this.$element.find("> .kt-portlet__head > .kt-portlet__head-toolbar > .btn.btn-clean.back");
        if (backLink.length > 0) url = backLink.attr("href");
        else {
            url = location.pathname;
            // eslint-disable-next-line no-useless-escape
            const findings = url.match(/^\/[a-zA-Z0-9-]*[^\/]/g);
            if (findings != null && findings.length !== 0) url = findings[0];
        }

        if (url === undefined || url.length === 0) return "";

        if (url.substr(url.length - 1, 1) !== "/") url = url + "/";

        return url;
    }

    private handlePostSaveAction(model: M): Promise<M> {
        return new Promise((resolve, reject) => {
            if (this["onPostSave"] === undefined) {
                resolve(model);
                return;
            }

            const subS = this["onPostSave"](model);
            if (subS === undefined) {
                resolve(model);
                return;
            }

            return subS
                .then((f) => resolve(f))
                .catch((ex) => {
                    logError(ex);
                    reject();
                });
        });
    }

    protected handleApiError(type: string, error?: DaoError) {
        let message = this["resolveErrorMessage"] !== undefined ? this["resolveErrorMessage"](type, error) : undefined;
        if (message === undefined) {
            switch (type) {
                case "read":
                    message = i18n("general.failedLoad");
                    break;
                case "create":
                    message = i18n("general.failedCreate");
                    break;
                case "update":
                    message = i18n("general.failedUpdate");
                    break;
                case "delete":
                    message = i18n("general.failedRemove");
                    break;
            }
        }

        UiNotification.showException(message);
    }

    protected scrollToFirstErrorField() {
        const invalidFields = this.$form.find(".is-invalid");

        if (invalidFields.length) {
            DomUtils.scrollTo(invalidFields.first());
        }
    }
}
